/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 } from "uuid";
import { StateContext } from "../../../context/globalContext/context";
import { iGlobalContext } from "../../../context/globalContext/interface";
import { iDataStreamingContext } from "../../../context/dataStreamingContext/dataStreamingContextInterface";
import { useDataStreamingContext } from "../../../context/dataStreamingContext/DataStreamingContext";
import { iSocketContext, useSocketContext } from "../../../context/socketContext/SocketContext";
import { useNavigationUrl } from "../../../hooks/useNavigationUrl";
import { GA_LABEL_ENUMS, triggerGAEvent } from "../../../constant/google-analytics/GAEvents";
import { useTheresiasEventBridge } from "../../../services/intent/useTheresiasEventBridge";
import { DialogSelected } from "../../../constant/common";
import { SELECTED_CHAT_DATA } from "../../../constant/dummyData";
import { setChatId } from "../../../utils/chat/chatId";
import { ROUTES } from "../../../constant";
import { ONBOARDING_OPEN_FROM } from "../../../enums";
import { getOnBoardingPrompt } from "../../../utils/itineraryUtils/getOnBoardingPrompt";
import { consoleLog } from "../../../utils/consoleLog/consoleLog";
import { FlightSelectedOptions, ONBOARDING_STEPS } from "../../../types";
import { usePocContext } from "../../../context/pocContext/pocContext";

export const useOnBoarding = () => {
  const state = useContext(StateContext);
  const {
    isMobile,
    setLoginCreateAccountState,
    onBoardingStep,
    setOnBoardingStep,
    onBoardForm,
    setChatData,
    setSelectedChatData,
    userInfo,
    setSelectedFlightOptions,
    setOnboardingOpenFrom,
    promptTemplate,
    setTriggeronBoardingEvent,
    templateDetails,
    setDestinationListGeneratedGAEventsFlag,
  }: iGlobalContext = state;

  const navigate = useNavigate();
  const { navigateWithParams } = useNavigationUrl();
  const location = useLocation();
  const fireTheresiasEvent = useTheresiasEventBridge();
  const { handleOpenSocketConnection }: iSocketContext = useSocketContext();
  const { setRegeneratingItinerary }: iDataStreamingContext = useDataStreamingContext();
  const { handleNewTripSendMessage } = usePocContext();

  const [progress, setProgress] = useState<number>(0);
  const [disableNext, setDisableNext] = useState<boolean>(false);

  // Browser history management — trap the Back button so the user stays in the
  // onboarding funnel. We seed a single dummy entry and re-arm it after a real
  // Back, but deliberately DROP the old navigate(location.pathname, { replace: true })
  // (the URL is already /onboarding, so that replaceState was redundant, and fired
  // on every popstate it was the call tripping Safari's "history.replaceState() >
  // 100 times / 10s" SecurityError). The re-arm is throttled so a rapid Back burst
  // can't exceed WebKit's history-mutation cap.
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);

    let lastRearmAt = 0;
    const REARM_COOLDOWN_MS = 350;
    const handlePopState = () => {
      const now = Date.now();
      if (now - lastRearmAt < REARM_COOLDOWN_MS) return;
      lastRearmAt = now;
      window.history.pushState(null, "", window.location.href);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate, location.pathname]);

  // Redirect logged-in users
  useEffect(() => {
    if (userInfo?.loggedIn) {
      navigateWithParams(ROUTES.Home);
    }
  }, []);

  // Migrate stale sessionStorage values (e.g. "welcome" from before that step
  // was removed) so handleNext / handleBack indexOf logic doesn't see -1 and
  // freeze the Next button on a returning user's tab.
  useEffect(() => {
    if (!(ONBOARDING_STEPS as readonly string[]).includes(onBoardingStep as unknown as string)) {
      setOnBoardingStep("lookingFor");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create itinerary streaming
  const handleCreateItineraryStreaming = useCallback(
    async (prompt: string) => {
      setSelectedChatData(SELECTED_CHAT_DATA);
      setChatData([
        {
          id: v4(),
          message: "",
          isUserMessage: true,
          prompt: [],
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
        },
        {
          id: v4(),
          message: "",
          isUserMessage: false,
          prompt: [],
          isLoading: true,
          destinationList: [],
          singleSelectedDestination: false,
          isCompleteMessage: false,
        },
      ]);
      const chatId = v4();
      setChatId(chatId);
      setDestinationListGeneratedGAEventsFlag(true);
      // REST-only: dispatch the onboarding-generated prompt through pocContext
      // (chatPocApi POST). Websocket open is short-circuited globally in SocketContext.
      // Hide the user bubble — this prompt is system context, not user-typed input.
      handleNewTripSendMessage(prompt, true);
      setRegeneratingItinerary(false);
    },
    [
      setChatData,
      setSelectedChatData,
      handleNewTripSendMessage,
      setRegeneratingItinerary,
      setDestinationListGeneratedGAEventsFlag,
    ]
  );

  // Handle next step
  const handleNext = useCallback(() => {
    const index = ONBOARDING_STEPS.indexOf(onBoardingStep);
    if (index === -1) return;
    const nextStep = ONBOARDING_STEPS[index + 1];
    if (nextStep) setOnBoardingStep(nextStep);

    switch (onBoardingStep) {
      case "home": {
        triggerGAEvent(GA_LABEL_ENUMS.onboarding_home_city_next_click, "", "", "", {
          city: onBoardForm?.home?.city ?? "",
          country: onBoardForm?.home?.country ?? "",
        });
        break;
      }
      case "travelWith": {
        triggerGAEvent(GA_LABEL_ENUMS.onboarding_travel_next_click);
        break;
      }
      case "lookingFor": {
        setChatData([]);
        triggerGAEvent(GA_LABEL_ENUMS.onboarding_vibe_next_click);
        // Spec §F: { vibes: string[] } — the full set of vibes the user
        // confirmed on Next, each UPPER_SNAKE_CASE (e.g. CITY_BUZZ).
        const toUpperSnake = (s: string) =>
          String(s || "")
            .trim()
            .toUpperCase()
            .replace(/[^A-Z0-9]+/g, "_")
            .replace(/^_|_$/g, "");
        const vibes = (onBoardForm?.lookingFor ?? []).map(toUpperSnake);
        fireTheresiasEvent("onboarding-vibe-next-click", { vibes });
        break;
      }
      case "budget": {
        setOnboardingOpenFrom({ from: ONBOARDING_OPEN_FROM.createDestinationList, data: "" });
        triggerGAEvent(GA_LABEL_ENUMS.onboarding_budget_next_click);

        const promptLocal = `
        I recently found out about Zenvoya and am trying it out for the first time to evaluate the product. Start by saying hi and introduce yourself by telling me you are there to assist me in my travel planning.

        Your job is to recommend the best and most relevant places for me to explore given my preferences. Think through your answer before responding. Provide a brief description why each place was recommended. Do not repeat any destinations. End your response by mentioning that I can update my travel preferences under the Preferences tab.

        ### Travel Preferences

        Flying from: <homecity>
        Travelling with: <travelwith>
        Vibes I look for: <comma-separated-list-of-selected-vibes>
        Budget: <budget-rating>


        ---

        Task: Based on my travel preferences, give 4 unique recommendations for the best and most relevant places for me to explore. If I have provided more than one vibe, provide at least one unique suggestion per vibe.
        `;
        const prompt = promptTemplate?.find((item: any) => item?.key === "user.first.chat")?.value ?? promptLocal;
        handleCreateItineraryStreaming(getOnBoardingPrompt(prompt, onBoardForm));
        setTriggeronBoardingEvent(true);
        break;
      }
      case "singUp": {
        break;
      }
      default: {
        break;
      }
    }
    consoleLog(onBoardingStep);
  }, [
    onBoardingStep,
    onBoardForm,
    setOnBoardingStep,
    setChatData,
    setOnboardingOpenFrom,
    promptTemplate,
    handleCreateItineraryStreaming,
    setTriggeronBoardingEvent,
  ]);

  // Handle back step
  const handleBack = useCallback(() => {
    const currentIndex = ONBOARDING_STEPS.indexOf(onBoardingStep);

    switch (onBoardingStep) {
      case "home": {
        triggerGAEvent(GA_LABEL_ENUMS.onboarding_home_city_back_click);
        break;
      }
      case "travelWith": {
        triggerGAEvent(GA_LABEL_ENUMS.onboarding_travel_back_click);
        break;
      }
      case "lookingFor": {
        triggerGAEvent(GA_LABEL_ENUMS.onboarding_vibe_back_click);
        break;
      }
      case "budget": {
        triggerGAEvent(GA_LABEL_ENUMS.onboarding_budget_back_click);
        break;
      }
      case "singUp": {
        break;
      }
      default: {
        break;
      }
    }

    if (currentIndex > 0) {
      setOnBoardingStep(ONBOARDING_STEPS[currentIndex - 1]);
    }
  }, [onBoardingStep, setOnBoardingStep]);

  // Handle login
  const handleLogin = useCallback(() => {
    // User just declared themselves an existing user — wipe the new-signup-only
    // travel-with snapshot so the WhoComingWithYou modal (App.tsx:231-240)
    // doesn't pop up after login. Without this, App.tsx's effect can fire on
    // userInfo.loggedIn change before the per-login-flow `removeItem` runs,
    // racing in the stale value and showing the modal.
    localStorage.removeItem("temp_onboarding_travelWith");
    triggerGAEvent(GA_LABEL_ENUMS.onboarding_log_in_click);

    setLoginCreateAccountState({
      flag: true,
      page: DialogSelected.LOGIN,
    });
  }, [setLoginCreateAccountState]);

  // Calculate disable next state. Switch on step name (not index) so reordering
  // ONBOARDING_STEPS doesn't silently break the validation gate for each screen.
  const handleDisableNext = useCallback(() => {
    const { city, country } = onBoardForm.home || {};
    let shouldDisable = false;

    switch (onBoardingStep) {
      case "lookingFor":
        shouldDisable = onBoardForm.lookingFor.length === 0;
        break;
      case "travelWith":
        shouldDisable = !onBoardForm.travelWith;
        break;
      case "budget":
        shouldDisable = false;
        break;
      case "home":
        shouldDisable =
          (!city || city.trim() === "") &&
          (!onBoardForm.home.state || onBoardForm.home.state.trim() === "") &&
          (!country || country.trim() === "");
        break;
      default:
        shouldDisable = false;
    }

    setDisableNext(shouldDisable);
  }, [onBoardForm, onBoardingStep]);

  // Update disable next on form changes
  useEffect(() => {
    handleDisableNext();
  }, [handleDisableNext]);

  // Update progress bar
  useEffect(() => {
    const effectiveSteps = ONBOARDING_STEPS.slice(0, -1);
    const index = effectiveSteps.indexOf(onBoardingStep);

    if (index === -1) return;

    setProgress((index / (effectiveSteps.length - 1)) * 100);
  }, [onBoardingStep]);

  // Handle URL-based step navigation
  useEffect(() => {
    if (onBoardingStep === "singUp") return;

    if (window.location.href.includes("book_flight")) {
      setOnBoardingStep("singUp");
      setSelectedFlightOptions({ flights: true, hotels: false, activities: false });
    }
    if (window.location.href.includes("book_stay")) {
      setOnBoardingStep("singUp");
      setSelectedFlightOptions({ flights: false, hotels: true, activities: false });
    }
    if (window.location.href.includes("book_flight_stay")) {
      setOnBoardingStep("singUp");
      setSelectedFlightOptions({ flights: true, hotels: true, activities: false });
    }
    if (window.location.href.includes("tid") && templateDetails?.type === "signUp") {
      setOnBoardingStep("singUp");
      const booking = templateDetails?.signUp?.booking;
      const bookingOptions: Record<string, FlightSelectedOptions> = {
        flight: { flights: true, hotels: true, activities: false },
        stay: { flights: false, hotels: true, activities: false },
        flightAndstay: { flights: true, hotels: true, activities: false },
      };

      if (booking && bookingOptions[booking]) {
        setSelectedFlightOptions(bookingOptions[booking]);
      }
    }

    // Direct shared-itinerary URL hit: /onboarding?itineraryId=<uuid> (without
    // going through the /itinerary or /shared redirect chain). Without this
    // jump, the user would land on `lookingFor` (the default first step) and
    // SignUpHeader would never mount — meaning the preview hook never fires
    // and no city / image / Trustpilot-on-signup is shown. Skip if URL also
    // has booking markers (those already handled above and shouldn't double-
    // fire on book_*&itineraryId combinations).
    if (
      window.location.search.includes("itineraryId=") &&
      !window.location.href.includes("book_flight") &&
      !window.location.href.includes("book_stay")
    ) {
      setOnBoardingStep("singUp");
    }
  }, [
    onBoardingStep,
    setOnBoardingStep,
    setSelectedFlightOptions,
    templateDetails?.signUp?.booking,
    templateDetails?.type,
  ]);

  return {
    // State
    isMobile,
    onBoardingStep,
    progress,
    disableNext,
    templateDetails,

    // Handlers
    handleNext,
    handleBack,
    handleLogin,
  };
};
